import React from "react";

export interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string;
  isLoading?: boolean;
  emptyMessage?: string;
  sortColumn?: string;
  sortDirection?: "ASC" | "DESC";
  onSort?: (column: string) => void;
  className?: string;
  headerClassName?: string;
  rowClassName?: (item: T, index: number) => string;
  onRowClick?: (item: T) => void;
}

const Table = <T extends object>({
  data,
  columns,
  keyExtractor,
  isLoading = false,
  emptyMessage = "Nessun dato trovato",
  sortColumn,
  sortDirection,
  onSort,
  className = "",
  headerClassName = "",
  rowClassName,
  onRowClick,
}: TableProps<T>) => {
  const getSortIcon = (column: string) => {
    const columnDef = columns.find((c) => c.key === column);
    if (!columnDef?.sortable || sortColumn !== column) return null;

    return sortDirection === "ASC" ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 inline-block ml-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4 inline-block ml-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <svg
          className="animate-spin h-8 w-8 mx-auto text-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="mt-2 text-gray-500">Caricamento dati...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className={`min-w-full divide-y divide-gray-200 ${className}`}>
        <thead className={`bg-gray-50 ${headerClassName}`}>
          <tr>
            {columns.map((column) => {
              const sortable = column.sortable && onSort;
              const headerClasses = `px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                sortable ? "cursor-pointer" : ""
              } ${column.className || ""}`;

              return (
                <th
                  key={column.key}
                  scope="col"
                  className={headerClasses}
                  onClick={sortable ? () => onSort(column.key) : undefined}
                >
                  {column.header} {sortable && getSortIcon(column.key)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item, index) => (
            <tr
              key={keyExtractor(item)}
              className={rowClassName ? rowClassName(item, index) : ""}
              onClick={() => onRowClick && onRowClick(item)}
            >
              {columns.map((column) => (
                <td
                  key={`${keyExtractor(item)}-${column.key}`}
                  className={`px-6 py-4 whitespace-nowrap ${
                    column.className || ""
                  }`}
                >
                  {column.render
                    ? column.render(item)
                    : (item[column.key as keyof T] as React.ReactNode)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
